import React, { FC, useState } from 'react';

import { StyleSheet, Text, View } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import { PullToReloadScrollView } from 'components/pull-to-reload/pull-to-reload';
import { useLoyaltyPoints } from 'hooks/loyalty/use-loyalty-points';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useAuthContext } from 'state/auth';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { primitive } from 'styles/constants/primitives';
import { WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { waitForDuration } from 'utils/wait-for-duration';

import { LoyaltyNotEnrolled } from '../loyalty-not-enrolled';
import { useFilteredRewardCategories } from '../loyalty-rewards/use-filtered-reward-categories';

import { useLastSeenLoyaltyPoints } from './profile-card/components/hooks/use-last-seen-loyalty-points';
import { ProfileCard } from './profile-card/profile-card';
import { ProfileCardSkeleton } from './profile-card/profile-card-skeleton';

/**
 *
 * LoyaltyProfile displays the user's loyalty profile
 *
 */
export const LoyaltyProfile: FC = () => {
  const [isForceRefreshing, setForceRefresh] = useState<boolean>(false);
  const { user, loading: loadingUser, isAuthenticated } = useAuthContext();
  const { loyaltyUser, loading: loadingLoyaltyUser } = useLoyaltyUser();
  const {
    loading: rewardDataIsLoading,
    sanityRewardsMap,
    engineRewardsMap,
    refetchRewards,
  } = useLoyaltyRewardsList();
  const rewardCategories = useFilteredRewardCategories();
  const { currentPoints } = useLoyaltyPoints(loyaltyUser);

  const loyaltyId = user?.loyaltyId;

  // Loads the last seen loyalty points from the local storage
  const { lastSeenLoyaltyPoints, refreshLastSeenLoyaltyPoints } = useLastSeenLoyaltyPoints(
    currentPoints
  );

  // Refetch for updated crowns when navigating to page
  useLoyaltyUserState({ refreshOnMountOrFocus: true });

  const forceRefresh = async () => {
    // We need this delay in order to avoid cutting the spinning animation
    await waitForDuration(1500);
    if (loyaltyId) {
      refreshLastSeenLoyaltyPoints();
      setForceRefresh(true);
      await refetchRewards(loyaltyId);
      setForceRefresh(false);
    }
  };

  if (isAuthenticated && !loyaltyId && !loadingUser) {
    return <LoyaltyNotEnrolled />;
  }

  const isLoading =
    loadingUser ||
    loadingLoyaltyUser ||
    rewardDataIsLoading ||
    isForceRefreshing ||
    !rewardCategories?.length ||
    !sanityRewardsMap ||
    lastSeenLoyaltyPoints === null;

  if (isLoading) {
    return (
      <View style={centeredRowContainerStyles.container}>
        <View style={styles.profileCardContainer}>
          <View style={styles.wrapper}>
            <ProfileCardSkeleton />
          </View>
        </View>
      </View>
    );
  }

  return (
    <PullToReloadScrollView
      progressViewOffset={10}
      onRefresh={forceRefresh}
      contentContainerStyle={centeredRowContainerStyles.container}
      isDisabled={!isAuthenticated}
    >
      {!isAuthenticated ? (
        <View>
          {/* TODO: Add a ProfileSignUp component */}
          <Text>ProfileSignUp component</Text>
        </View>
      ) : (
        <>
          <View style={styles.profileCardContainer}>
            <View style={styles.wrapper}>
              <ProfileCard
                loyaltyUser={loyaltyUser}
                engineRewardsMap={engineRewardsMap}
                rewardCategories={rewardCategories}
                lastSeenLoyaltyPoints={lastSeenLoyaltyPoints}
              />
            </View>
          </View>
        </>
      )}
    </PullToReloadScrollView>
  );
};

const styles = StyleSheet.create({
  profileCardContainer: {
    width: '100%',
    display: 'flex',
    paddingTop: primitive.spacing.$7,
    paddingBottom: primitive.spacing.$6,
    backgroundColor: primitive.bk.$lightMustard,
  },
  wrapper: {
    ...centeredRowContainerStyles.content,
    paddingHorizontal: primitive.spacing.$4,
    maxWidth: WEB_SMALL_MAX_WIDTH,
  },
});
